import { useState } from "react"
import { toast } from "sonner"

import { useAliases, useGuardarAlias, usePrecios } from "@/api/queries"
import type { Alias, Reasoning } from "@/api/types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

//: `""` en `reasoning` representa el `null` del gateway —«no tocar la petición»—
//: porque un `<Select>` no admite valor nulo. Se traduce al enviar.
type Borrador = {
  alias: string
  provider_id: string
  model_id: string
  enabled: boolean
  reasoning: string
}

const NUEVO: Borrador = {
  alias: "",
  provider_id: "deepseek",
  model_id: "",
  enabled: true,
  reasoning: "",
}

const RAZONAMIENTO: { valor: string; etiqueta: string }[] = [
  { valor: "", etiqueta: "Lo que decida el proveedor" },
  { valor: "disabled", etiqueta: "Sin razonamiento" },
  { valor: "low", etiqueta: "Esfuerzo bajo" },
  { valor: "high", etiqueta: "Esfuerzo alto" },
  { valor: "max", etiqueta: "Esfuerzo máximo" },
]

export function AliasesPage() {
  const aliases = useAliases()
  const precios = usePrecios()
  const guardar = useGuardarAlias()

  const [borrador, setBorrador] = useState<Borrador | null>(null)
  const [esNuevo, setEsNuevo] = useState(false)

  // Un alias sin precio vigente existe pero rechaza con PRICE_MISSING. Es la
  // diferencia entre "no existe" y "no puedo cobrarlo todavía", y ahorra el rato de
  // depurar por qué un alias configurado no responde.
  const conPrecio = new Set((precios.data ?? []).filter((p) => p.vigente).map((p) => p.alias))

  function abrir(a: Alias | null) {
    setEsNuevo(a === null)
    setBorrador(
      a === null
        ? { ...NUEVO }
        : {
            alias: a.alias,
            provider_id: a.provider_id,
            model_id: a.model_id,
            enabled: a.enabled,
            reasoning: a.reasoning ?? "",
          },
    )
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    if (borrador === null) return
    try {
      await guardar.mutateAsync({
        alias: borrador.alias,
        datos: {
          provider_id: borrador.provider_id,
          model_id: borrador.model_id,
          enabled: borrador.enabled,
          // Vacío vuelve a ser `null`: "no dice nada" no es lo mismo que "sin
          // razonamiento", y mandar `disabled` por defecto cambiaría el comportamiento
          // de un alias que nadie configuró.
          reasoning: borrador.reasoning === "" ? null : (borrador.reasoning as Reasoning),
        },
      })
      toast.success(esNuevo ? "Alias creado" : "Alias actualizado")
      setBorrador(null)
    } catch {
      toast.error("No se pudo guardar el alias")
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Alias de modelos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Las aplicaciones piden un alias, nunca un modelo. Cambiar de modelo es una fila
            aquí, no un despliegue de cada aplicación.
          </p>
        </div>
        <Button onClick={() => abrir(null)}>Nuevo alias</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {aliases.isPending ? (
            <Skeleton className="h-40" />
          ) : aliases.isError ? (
            <p className="text-sm text-destructive">No se pudieron cargar los alias.</p>
          ) : aliases.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Todavía no hay alias configurados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alias</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aliases.data.map((a) => (
                    <TableRow key={a.alias}>
                      <TableCell className="font-medium">{a.alias}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {a.provider_id}/{a.model_id}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={a.enabled ? "secondary" : "outline"}>
                            {a.enabled ? "activo" : "deshabilitado"}
                          </Badge>
                          {!conPrecio.has(a.alias) && (
                            <Badge variant="destructive">sin precio</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => abrir(a)}>
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!precios.isPending && aliases.data?.some((a) => !conPrecio.has(a.alias)) && (
            <p className="mt-4 text-xs text-muted-foreground">
              Un alias sin precio vigente existe pero <strong>no despacha</strong>: rechaza
              la petición en vez de cobrarla a un precio inventado.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={borrador !== null} onOpenChange={(abierto) => !abierto && setBorrador(null)}>
        <DialogContent>
          <form onSubmit={enviar}>
            <DialogHeader>
              <DialogTitle>{esNuevo ? "Nuevo alias" : `Editar ${borrador?.alias}`}</DialogTitle>
              <DialogDescription>
                Nómbralo por intención (<code>chat-default</code>), nunca por proveedor o
                versión.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="alias">Alias</Label>
                <Input
                  id="alias"
                  value={borrador?.alias ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => (b === null ? b : { ...b, alias: e.target.value }))
                  }
                  disabled={!esNuevo}
                  placeholder="chat-default"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provider">Proveedor</Label>
                <Input
                  id="provider"
                  value={borrador?.provider_id ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => (b === null ? b : { ...b, provider_id: e.target.value }))
                  }
                  placeholder="deepseek"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Modelo del proveedor</Label>
                <Input
                  id="model"
                  value={borrador?.model_id ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => (b === null ? b : { ...b, model_id: e.target.value }))
                  }
                  placeholder="deepseek-chat"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reasoning">Razonamiento</Label>
                <select
                  id="reasoning"
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={borrador?.reasoning ?? ""}
                  onChange={(e) =>
                    setBorrador((b) => (b === null ? b : { ...b, reasoning: e.target.value }))
                  }
                >
                  {RAZONAMIENTO.map((o) => (
                    <option key={o.valor} value={o.valor}>
                      {o.etiqueta}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Lo que el modelo piensa <strong>gasta del mismo `max_tokens` que la
                  respuesta</strong>. Con un presupuesto ajustado, dejarlo por defecto puede
                  devolver respuestas vacías de forma intermitente. Si el cliente manda el
                  parámetro él mismo, manda el cliente.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="enabled"
                  checked={borrador?.enabled ?? true}
                  onCheckedChange={(v) =>
                    setBorrador((b) => (b === null ? b : { ...b, enabled: v }))
                  }
                />
                <Label htmlFor="enabled" className="font-normal">
                  Activo
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBorrador(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={guardar.isPending}>
                {guardar.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
